import dayjs, { dateDisplayFormat, dateTimeDisplayFormat } from '~libs/dayjs';

/**
 * Shared formatter for the collection_date / created_at fields surfaced across
 * the Payment Batches feature (list, detail). These are event timestamps, so
 * the time of day is retained. Renders falsy values and parse failures as a
 * dash so the table never shows a literal "Invalid Date" string for malformed
 * input.
 */
export const formatBatchDate = (value: string | null | undefined): string => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(dateTimeDisplayFormat) : '-';
};

/**
 * Invoice dates carry no time of day (the stored value is always midnight), so
 * rendering a clock time would invent precision on screens about money. Date
 * only, delegating to formatBatchDate for the missing / unparseable placeholder
 * so both formatters render the same fallback.
 *
 * The backend's invoice_date is a DATEONLY column, always serialized as a bare
 * YYYY-MM-DD string today, but this formatter takes the date-only prefix
 * before parsing regardless. A Z-suffixed UTC timestamp parsed directly with
 * dayjs (no timezone plugin registered in ~libs/dayjs) is rendered in the
 * browser's local time, which can shift the displayed calendar date backward
 * for any timezone behind UTC. Both a bare YYYY-MM-DD value and a Z-suffixed
 * ISO timestamp share the same leading 10 characters, so slicing before
 * parsing sidesteps that shift without pulling in a new dayjs plugin.
 */
export const formatBatchInvoiceDate = (value: string | null | undefined): string => {
  const datePart = value ? value.slice(0, 10) : value;
  const parsed = dayjs(datePart);
  return datePart && parsed.isValid() ? parsed.format(dateDisplayFormat) : formatBatchDate(value);
};
